import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Edit, Trash2, Search, Package, Upload, X, Loader2, Check, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { CATEGORIES as FALLBACK_CATEGORIES, COLOR_PALETTE as FALLBACK_COLORS, AVAILABILITY_OPTIONS } from "@/lib/constants";
import { toast } from "sonner";

const blank = {
  sku: "",
  name: "",
  category: "huellen",
  brand: "",
  compatible: "",
  compatible_brands: [],
  compatible_models: [],
  colors: [],
  images: [],
  price: 0,
  stock: 0,
  availability: "available",
  on_sale: false,
  sale_price: null,
  description: "",
  image: "",
  rating: 4.5,
  featured: false,
};

function toAbsoluteImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  const loadMeta = async () => {
    const [cats, cls] = await Promise.all([
      api.get("/categories").catch(() => ({ data: [] })),
      api.get("/colors").catch(() => ({ data: [] })),
    ]);
    setCategories(cats.data);
    setColors(cls.data);
  };
  useEffect(() => { load(); loadMeta(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...blank, category: categories[0]?.id || "huellen" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p.sku);
    setForm({
      ...blank,
      ...p,
      images: p.images && p.images.length ? p.images : (p.image ? [p.image] : []),
      colors: p.colors || [],
      // Migrate legacy single-brand to list
      compatible_brands: (p.compatible_brands && p.compatible_brands.length)
        ? p.compatible_brands
        : (p.compatible_brand ? [p.compatible_brand] : []),
      compatible_models: p.compatible_models || [],
      on_sale: !!p.on_sale,
      sale_price: p.sale_price ?? null,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.price) {
      toast.error("Bitte Name und Preis ausfüllen");
      return;
    }
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        stock: Number(form.stock || 0),
        rating: Number(form.rating || 4.5),
        sale_price: form.on_sale && form.sale_price ? Number(form.sale_price) : null,
        image: form.images?.[0] || form.image || "",
      };
      if (editing) {
        await api.patch(`/admin/products/${editing}`, payload);
        toast.success("Produkt aktualisiert");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produkt erstellt");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const del = async (sku) => {
    if (!window.confirm("Produkt wirklich löschen?")) return;
    await api.delete(`/admin/products/${sku}`);
    toast.success("Gelöscht");
    load();
  };

  const filtered = products.filter((p) => !search || `${p.name} ${p.sku} ${p.brand}`.toLowerCase().includes(search.toLowerCase()));

  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const runAudit = async () => {
    setAuditing(true);
    try {
      const r = await api.get("/admin/audit/broken-images");
      setAudit(r.data);
      if (r.data.products_with_issues === 0) {
        toast.success(`Alle ${r.data.total_products} Produkte haben gültige Bilder ✅`);
      } else {
        toast.warning(`${r.data.products_with_issues} Produkt(e) mit fehlenden Bildern gefunden`);
      }
    } catch (e) {
      toast.error("Audit fehlgeschlagen");
    } finally {
      setAuditing(false);
    }
  };
  const brokenSkus = new Set((audit?.items || []).map((x) => x.sku));

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Shop</div>
          <h1 className="font-display text-3xl font-bold text-white mt-1">Produkte</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={runAudit}
            disabled={auditing}
            variant="outline"
            className="bg-transparent border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white rounded-full"
            data-testid="audit-images-btn"
          >
            {auditing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Shield className="w-4 h-4 mr-1.5" />}
            Bilder-Audit
          </Button>
          <Button onClick={openCreate} className="bg-[#DC1928] hover:bg-[#B01829] rounded-full text-white" data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Neues Produkt
          </Button>
        </div>
      </div>

      {audit && audit.products_with_issues > 0 && (
        <div className="mb-5 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30" data-testid="audit-banner">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-semibold text-amber-200">
                {audit.products_with_issues} Produkt(e) mit fehlenden Bildern
              </div>
              <div className="text-xs text-zinc-400 mt-1">
                Diese Bilder sind nicht (mehr) im persistenten Storage (GridFS). Bitte für jedes Produkt unten ein neues Bild hochladen — danach werden sie sicher gespeichert.
              </div>
              <div className="text-[10px] text-zinc-500 mt-2">
                GridFS-Bestand gesamt: {audit.gridfs_files_total} Bilder · Produkte gesamt: {audit.total_products}
              </div>
            </div>
            <button
              onClick={() => setAudit(null)}
              className="text-zinc-500 hover:text-white"
              aria-label="Schließen"
            ><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      <div className="bg-[#141417] rounded-2xl border border-white/5 p-4 mb-4 relative">
        <Search className="w-4 h-4 text-zinc-500 absolute left-7 top-1/2 -translate-y-1/2" />
        <Input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-[#0A0A0B] border-white/10 text-white" data-testid="products-search" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((p) => {
          const isBroken = brokenSkus.has(p.sku);
          return (
          <div key={p.sku} className={`bg-[#141417] rounded-2xl border overflow-hidden transition-colors ${isBroken ? "border-amber-500/40 ring-1 ring-amber-500/15" : "border-white/5 hover:border-white/15"}`} data-testid={`product-row-${p.sku}`}>
            <div className="flex">
              <div className="w-28 sm:w-32 aspect-square bg-[#0A0A0B] shrink-0 relative border-r border-white/5">
                {p.image ? (
                  <img
                    src={toAbsoluteImageUrl(p.image)}
                    alt=""
                    className="absolute inset-0 w-full h-full object-contain p-1.5"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextElementSibling?.classList.remove("hidden");
                    }}
                  />
                ) : null}
                <div className={`${p.image ? "hidden" : ""} absolute inset-0 flex flex-col items-center justify-center text-zinc-600 gap-1.5 p-2`}>
                  <Package className="w-7 h-7 opacity-50" />
                  <span className="text-[9px] uppercase tracking-wider text-center leading-tight">Kein Foto</span>
                </div>
                {p.images?.length > 1 && (
                  <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/70 text-white text-[10px]">+{p.images.length - 1}</span>
                )}
              </div>
              <div className="flex-1 p-4 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500 truncate flex-1 min-w-0" title={p.sku}>{p.sku}</div>
                  <AvailabilityBadge value={p.availability} />
                </div>
                {isBroken && (
                  <div className="flex items-center gap-1 mt-1 text-[10px] font-bold text-amber-300 uppercase tracking-wider" data-testid={`broken-img-${p.sku}`}>
                    <Shield className="w-2.5 h-2.5" />
                    Bild fehlt — bitte neu hochladen
                  </div>
                )}
                <div className="font-display font-semibold text-white mt-1 leading-tight line-clamp-2 text-sm" title={p.name}>{p.name}</div>
                <div className="text-xs text-zinc-500 mt-1.5 line-clamp-1">
                  {p.brand}{p.brand && p.compatible ? " · " : ""}{p.compatible}
                </div>
                {(p.colors?.length > 0) && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {p.colors.slice(0, 8).map((cId) => {
                      const c = (colors.length ? colors : FALLBACK_COLORS).find((x) => x.id === cId);
                      if (!c) return null;
                      return (
                        <span key={cId} className="w-3 h-3 rounded-full border border-white/20" style={{ background: c.hex === "transparent" ? "repeating-conic-gradient(#444 0% 25%, #aaa 0% 50%) 50% / 6px 6px" : c.hex }} title={c.name} />
                      );
                    })}
                    {p.colors.length > 8 && (
                      <span className="text-[9px] text-zinc-500 self-center ml-0.5">+{p.colors.length - 8}</span>
                    )}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-baseline gap-1.5">
                    {p.on_sale && p.sale_price && p.sale_price < p.price && (
                      <span className="text-xs text-zinc-500 line-through numeric">€{p.price?.toFixed(2)}</span>
                    )}
                    <span className={`font-display font-bold numeric ${p.on_sale ? "text-[#FF2D40]" : "text-white"}`}>
                      €{(p.on_sale && p.sale_price ? p.sale_price : p.price)?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} className="text-zinc-400 hover:text-white hover:bg-white/5 h-8 w-8" data-testid={`edit-${p.sku}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(p.sku)} className="hover:bg-white/5 h-8 w-8" data-testid={`del-${p.sku}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-zinc-500">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
            Keine Produkte gefunden
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-[#141417] border-white/10 text-white" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? "Produkt bearbeiten" : "Neues Produkt"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              {editing ? `SKU: ${editing}` : "SKU wird automatisch aus dem Namen generiert"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <MultiImageDrop
              value={form.images}
              onChange={(updater) =>
                setForm((f) => ({
                  ...f,
                  images: typeof updater === "function" ? updater(f.images || []) : updater,
                }))
              }
            />

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0B] border-white/10 text-white" data-testid="form-name" />
              </div>

              <div>
                <Label className="text-zinc-400">Kategorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-[#0A0A0B] border-white/10 text-white" data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141417] border-white/10 text-white">
                    {(categories.length ? categories : FALLBACK_CATEGORIES).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-zinc-400">Marke (Hersteller)</Label>
                <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Apple, Samsung, …" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="form-brand" />
              </div>

              <div>
                <Label className="text-zinc-400">Preis (€) *</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#0A0A0B] border-white/10 text-white" data-testid="form-price" />
              </div>

              <div className="flex items-end gap-2">
                <input type="checkbox" id="featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" className="accent-[#DC1928] w-4 h-4" />
                <Label htmlFor="featured" className="text-zinc-300">Als Bestseller markieren</Label>
              </div>

              <div className="col-span-2">
                <Label className="text-zinc-400">Verfügbarkeit</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="form-availability">
                  {AVAILABILITY_OPTIONS.map((opt) => {
                    const active = (form.availability || "available") === opt.id;
                    const colorBy = {
                      emerald: { dot: "bg-emerald-500", ring: "border-emerald-500 bg-emerald-500/10", text: "text-emerald-300" },
                      amber:   { dot: "bg-amber-500",   ring: "border-amber-500 bg-amber-500/10",   text: "text-amber-300" },
                      zinc:    { dot: "bg-zinc-500",    ring: "border-zinc-500 bg-zinc-500/10",    text: "text-zinc-300" },
                    }[opt.color];
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setForm({ ...form, availability: opt.id })}
                        className={`text-left p-3 rounded-xl border transition-all ${
                          active ? colorBy.ring : "border-white/10 bg-[#0A0A0B] hover:border-white/30"
                        }`}
                        data-testid={`availability-${opt.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${colorBy.dot}`}></span>
                          <span className={`text-sm font-display font-semibold ${active ? colorBy.text : "text-white"}`}>{opt.label}</span>
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-1">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="col-span-2">
                <Label className="text-zinc-400">Beschreibung</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="bg-[#0A0A0B] border-white/10 text-white" data-testid="form-description" />
              </div>
            </div>

            <ColorPicker value={form.colors} onChange={(colors) => setForm({ ...form, colors })} palette={colors} fallback={FALLBACK_COLORS} />

            <SalePicker
              onSale={form.on_sale}
              salePrice={form.sale_price}
              currentPrice={Number(form.price) || 0}
              onChange={({ on_sale, sale_price }) =>
                setForm((f) => ({
                  ...f,
                  on_sale: on_sale !== undefined ? on_sale : f.on_sale,
                  sale_price: sale_price !== undefined ? sale_price : f.sale_price,
                }))
              }
            />

            <CompatibilityPicker
              brands={form.compatible_brands}
              models={form.compatible_models}
              compatibleText={form.compatible}
              onChange={({ brands, models, compatibleText }) =>
                setForm((f) => ({
                  ...f,
                  compatible_brands: brands !== undefined ? brands : f.compatible_brands,
                  compatible_models: models !== undefined ? models : f.compatible_models,
                  compatible: compatibleText !== undefined ? compatibleText : f.compatible,
                }))
              }
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white">Abbrechen</Button>
            <Button onClick={save} className="bg-[#DC1928] hover:bg-[#B01829] text-white" data-testid="form-save">
              {editing ? "Speichern" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MultiImageDrop({ value, onChange }) {
  const [drag, setDrag] = useState(false);
  // Granular progress so the user always sees what's happening, even when
  // uploading 5 large files in a row (race-free thanks to functional setState).
  const [progress, setProgress] = useState({ done: 0, total: 0, currentName: "" });
  const inputRef = useRef(null);

  const uploadFiles = async (rawFiles) => {
    if (!rawFiles || rawFiles.length === 0) return;
    const arr = Array.from(rawFiles);
    const MAX = 16 * 1024 * 1024;  // 16 MB per file (matches backend cap)
    const ALLOWED = /\.(jpe?g|png|webp|gif)$/i;

    // Pre-validate and only keep valid files. Report every rejection.
    const valid = [];
    for (const f of arr) {
      if (!ALLOWED.test(f.name)) {
        toast.error(`Übersprungen: ${f.name} — Format nicht unterstützt (nur PNG/JPG/WEBP/GIF)`);
        continue;
      }
      if (f.size > MAX) {
        toast.error(`Übersprungen: ${f.name} — zu groß (${(f.size / (1024*1024)).toFixed(1)} MB · max 16 MB)`);
        continue;
      }
      valid.push(f);
    }
    if (valid.length === 0) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setProgress({ done: 0, total: valid.length, currentName: valid[0].name });
    let succeeded = 0;
    let failed = 0;

    // Upload ONE file at a time so a single backend error never wipes out a
    // batch, and so each successful file is added to the grid immediately
    // (visible feedback even mid-batch).
    for (let i = 0; i < valid.length; i++) {
      const file = valid[i];
      setProgress({ done: i, total: valid.length, currentName: file.name });
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60_000,
        });
        const url = res.data?.url;
        if (!url) throw new Error("Server lieferte keine URL zurück");
        // Race-free: functional update against the LATEST state.
        onChange((prev) => [...(prev || []), url]);
        succeeded += 1;
      } catch (e) {
        failed += 1;
        const reason = e?.response?.data?.detail || e?.message || "Upload fehlgeschlagen";
        toast.error(`Fehler bei "${file.name}": ${reason}`);
      }
    }

    setProgress({ done: 0, total: 0, currentName: "" });
    if (inputRef.current) inputRef.current.value = "";
    if (succeeded > 0) {
      toast.success(`${succeeded} Bild${succeeded === 1 ? "" : "er"} hochgeladen${failed ? ` · ${failed} fehlgeschlagen` : ""}`);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    uploadFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    onChange((prev) => (prev || []).filter((_, i) => i !== idx));
  };

  const makePrimary = (idx) => {
    onChange((prev) => {
      const arr = [...(prev || [])];
      const [picked] = arr.splice(idx, 1);
      arr.unshift(picked);
      return arr;
    });
  };

  const uploading = progress.total > 0;

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label className="text-zinc-400">Produktbilder</Label>
        <span className="text-[10px] text-zinc-500">{(value || []).length} Bild{(value || []).length === 1 ? "" : "er"} · erstes Bild = Hauptbild</span>
      </div>

      {/* Grid of existing images */}
      {(value && value.length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5 mb-2" data-testid="form-images-grid">
          {value.map((url, idx) => (
            <div key={url + idx} className={`relative aspect-square rounded-xl overflow-hidden border ${idx === 0 ? "border-[#DC1928]" : "border-white/10"} bg-[#0A0A0B] group`} data-testid={`form-image-${idx}`}>
              <img src={toAbsoluteImageUrl(url)} alt="" className="absolute inset-0 w-full h-full object-contain p-1" />
              {idx === 0 && (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md bg-[#DC1928] text-white text-[9px] font-semibold uppercase tracking-wide">Haupt</span>
              )}
              <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/0 group-hover:bg-black/55 opacity-0 group-hover:opacity-100 transition">
                {idx !== 0 && (
                  <button
                    type="button"
                    onClick={() => makePrimary(idx)}
                    className="w-7 h-7 rounded-full bg-white/90 text-black flex items-center justify-center hover:bg-white"
                    title="Als Hauptbild"
                    data-testid={`make-primary-${idx}`}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-500"
                  title="Entfernen"
                  data-testid={`remove-image-${idx}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative rounded-2xl border-2 border-dashed transition-colors overflow-hidden ${
          uploading ? "cursor-wait" : "cursor-pointer"
        } ${
          drag ? "border-[#DC1928] bg-[#DC1928]/5" : "border-white/15 bg-[#0A0A0B] hover:border-white/30"
        }`}
        data-testid="form-image-drop"
      >
        <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
          {uploading ? (
            <>
              <Loader2 className="w-7 h-7 text-[#FF2D40] animate-spin" />
              <div className="mt-3 text-sm font-medium text-white">
                Bild {progress.done + 1} / {progress.total} wird hochgeladen…
              </div>
              <div className="text-[11px] text-zinc-500 mt-1 max-w-[420px] truncate" title={progress.currentName}>
                {progress.currentName}
              </div>
              {/* Progress bar */}
              <div className="mt-3 w-48 h-1 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-[#DC1928] transition-all duration-300"
                  style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }}
                />
              </div>
            </>
          ) : (
            <>
              <Upload className="w-7 h-7 text-zinc-500" />
              <div className="mt-3 text-sm font-medium text-white">
                Bilder hierher ziehen oder klicken
              </div>
              <div className="text-xs text-zinc-500 mt-1">Mehrere Bilder gleichzeitig möglich · PNG, JPG, WebP, GIF · max. 16 MB pro Bild</div>
            </>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/*"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          disabled={uploading}
          data-testid="form-image-input"
        />
      </div>
    </div>
  );
}

function ColorPicker({ value, onChange, palette, fallback }) {
  const list = palette && palette.length ? palette : fallback;
  const toggle = (id) => {
    const current = value || [];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    onChange(next);
  };
  return (
    <div data-testid="form-colors">
      <div className="flex items-baseline justify-between">
        <Label className="text-zinc-400">Farben</Label>
        <span className="text-[10px] text-zinc-500">{(value || []).length} ausgewählt</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-1.5">
        {list.map((c) => {
          const active = (value || []).includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => toggle(c.id)}
              className={`group flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border text-xs transition-all ${
                active
                  ? "border-[#DC1928] bg-[#DC1928]/10 text-white"
                  : "border-white/10 bg-[#0A0A0B] text-zinc-400 hover:border-white/30 hover:text-white"
              }`}
              data-testid={`color-${c.id}`}
            >
              <span
                className="w-5 h-5 rounded-full border border-white/20 shrink-0"
                style={{
                  background:
                    c.hex === "transparent"
                      ? "repeating-conic-gradient(#444 0% 25%, #aaa 0% 50%) 50% / 8px 8px"
                      : c.hex,
                }}
              />
              {c.name}
              {active && <Check className="w-3 h-3 text-[#FF2D40] ml-0.5" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SalePicker({ onSale, salePrice, currentPrice, onChange }) {
  const discount = onSale && salePrice && currentPrice > salePrice
    ? Math.round(((currentPrice - Number(salePrice)) / currentPrice) * 100)
    : 0;
  return (
    <div className={`rounded-2xl border p-4 transition-colors ${onSale ? "border-[#DC1928]/40 bg-[#DC1928]/5" : "border-white/10 bg-[#0F0F11]"}`} data-testid="form-sale">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={onSale}
            onClick={() => onChange({ on_sale: !onSale })}
            className={`relative w-11 h-6 rounded-full transition-colors ${onSale ? "bg-[#DC1928]" : "bg-white/15"}`}
            data-testid="form-sale-toggle"
          >
            <span className={`absolute top-0.5 ${onSale ? "left-[22px]" : "left-0.5"} w-5 h-5 rounded-full bg-white transition-all`} />
          </button>
          <div>
            <Label className="text-zinc-200 text-sm font-semibold cursor-pointer" onClick={() => onChange({ on_sale: !onSale })}>SALE-Aktion</Label>
            <div className="text-[10px] text-zinc-500">Zeigt im Shop ein rotes &bdquo;SALE&ldquo;-Badge. Optional: reduzierter Aktionspreis.</div>
          </div>
        </div>
        {onSale && discount > 0 && (
          <span className="px-2 py-0.5 rounded-md bg-[#DC1928] text-white text-[10px] font-bold uppercase tracking-wide">−{discount}%</span>
        )}
      </div>
      {onSale && (
        <div className="mt-3 grid grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-500 text-xs">Aktionspreis (€) <span className="text-zinc-600">optional</span></Label>
            <Input
              type="number"
              step="0.01"
              value={salePrice ?? ""}
              onChange={(e) => onChange({ sale_price: e.target.value === "" ? null : e.target.value })}
              placeholder={`niedriger als €${currentPrice.toFixed(2)}`}
              className="bg-[#0A0A0B] border-white/10 text-white"
              data-testid="form-sale-price"
            />
          </div>
          <div className="text-[11px] text-zinc-500 self-end pb-2">
            Lass das Feld leer für ein reines SALE-Badge ohne Preisänderung.
          </div>
        </div>
      )}
    </div>
  );
}

function CompatibilityPicker({ brands, models, compatibleText, onChange }) {
  const [allBrands, setAllBrands] = useState([]);
  // Map<brand_id, string[]> of available models per brand
  const [modelsByBrand, setModelsByBrand] = useState({});
  const [loadingBrand, setLoadingBrand] = useState(null);

  const fetchBrands = useCallback(async () => {
    try {
      const r = await api.get("/catalog/brands");
      setAllBrands(r.data || []);
    } catch {
      setAllBrands([]);
    }
  }, []);

  const fetchModelsForBrand = useCallback(async (brandId) => {
    if (!brandId) return;
    setLoadingBrand(brandId);
    try {
      const r = await api.get(`/catalog/models/${brandId}`);
      setModelsByBrand((prev) => ({ ...prev, [brandId]: r.data || [] }));
    } catch {
      setModelsByBrand((prev) => ({ ...prev, [brandId]: [] }));
    } finally {
      setLoadingBrand(null);
    }
  }, []);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);
  // load models for each selected brand once
  useEffect(() => {
    (brands || []).forEach((b) => {
      if (!(b in modelsByBrand)) fetchModelsForBrand(b);
    });
  }, [brands, modelsByBrand, fetchModelsForBrand]);

  const toggleBrand = (brandId) => {
    const current = brands || [];
    const next = current.includes(brandId) ? current.filter((x) => x !== brandId) : [...current, brandId];
    // when removing a brand, also remove its models from the selection
    let nextModels = models || [];
    if (!next.includes(brandId)) {
      const removeModels = new Set(modelsByBrand[brandId] || []);
      nextModels = nextModels.filter((m) => !removeModels.has(m));
    }
    onChange({ brands: next, models: nextModels });
  };

  const toggleModel = (m) => {
    const current = models || [];
    const next = current.includes(m) ? current.filter((x) => x !== m) : [...current, m];
    onChange({ models: next });
  };

  const selectAllForBrand = (brandId) => {
    const brandModels = modelsByBrand[brandId] || [];
    const current = new Set(models || []);
    brandModels.forEach((m) => current.add(m));
    onChange({ models: Array.from(current) });
  };

  const clearAllForBrand = (brandId) => {
    const brandModels = new Set(modelsByBrand[brandId] || []);
    onChange({ models: (models || []).filter((m) => !brandModels.has(m)) });
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F11] p-4" data-testid="form-compatibility">
      <div className="flex items-baseline justify-between">
        <Label className="text-zinc-400 text-sm font-semibold">Kompatibilität</Label>
        <span className="text-[10px] text-zinc-500">Mehrere Marken möglich — Zubehör kann zu mehreren passen</span>
      </div>

      {/* Brand chips - multi-select */}
      <div className="mt-3">
        <Label className="text-zinc-500 text-xs">Marken</Label>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {allBrands.map((b) => {
            const active = (brands || []).includes(b.id);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => toggleBrand(b.id)}
                className={`px-3 py-1 rounded-full border text-xs transition-all ${
                  active
                    ? "border-[#DC1928] bg-[#DC1928]/10 text-white"
                    : "border-white/10 bg-[#0A0A0B] text-zinc-400 hover:border-white/30 hover:text-white"
                }`}
                data-testid={`form-compat-brand-${b.id}`}
              >
                {b.name}
                {active && <Check className="w-3 h-3 inline ml-1 text-[#FF2D40]" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <Label className="text-zinc-500 text-xs">Freitext (optional)</Label>
        <Input
          value={compatibleText || ""}
          onChange={(e) => onChange({ compatibleText: e.target.value })}
          placeholder='z.B. "universal", "USB-C", "alle iPhones 12-15"'
          className="bg-[#0A0A0B] border-white/10 text-white"
          data-testid="form-compatible"
        />
      </div>

      {/* Models per selected brand */}
      {(brands || []).map((brandId) => {
        const brandObj = allBrands.find((b) => b.id === brandId);
        const brandModels = modelsByBrand[brandId];
        const isLoading = loadingBrand === brandId;
        const selectedForBrand = (models || []).filter((m) => (brandModels || []).includes(m));
        return (
          <div key={brandId} className="mt-4 pt-4 border-t border-white/5">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <span className="text-xs uppercase tracking-wider text-[#FF2D40] font-bold">{brandObj?.name || brandId}</span>
                {selectedForBrand.length > 0 && (
                  <span className="text-[10px] text-zinc-500 ml-2">{selectedForBrand.length} von {brandModels?.length || 0} ausgewählt</span>
                )}
              </div>
              <div className="flex gap-2 text-[10px]">
                <button type="button" onClick={() => selectAllForBrand(brandId)} className="text-zinc-400 hover:text-white">Alle</button>
                <span className="text-zinc-700">|</span>
                <button type="button" onClick={() => clearAllForBrand(brandId)} className="text-zinc-400 hover:text-white">Leeren</button>
              </div>
            </div>
            {isLoading ? (
              <div className="flex items-center gap-2 text-xs text-zinc-500 py-3">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lade Modelle…
              </div>
            ) : !brandModels || brandModels.length === 0 ? (
              <div className="text-xs text-zinc-500 py-2">Keine Modelle hinterlegt.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto pr-1">
                {brandModels.map((m) => {
                  const active = (models || []).includes(m);
                  return (
                    <button
                      key={`${brandId}-${m}`}
                      type="button"
                      onClick={() => toggleModel(m)}
                      className={`px-2.5 py-1 rounded-full border text-[11px] transition-all ${
                        active
                          ? "border-[#DC1928] bg-[#DC1928]/10 text-white"
                          : "border-white/10 bg-[#0A0A0B] text-zinc-400 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {m}
                      {active && <Check className="w-3 h-3 inline ml-1 text-[#FF2D40]" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function AvailabilityBadge({ value }) {
  const opt = AVAILABILITY_OPTIONS.find((o) => o.id === (value || "available")) || AVAILABILITY_OPTIONS[0];
  const styles = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber:   "bg-amber-500/15   text-amber-300   border-amber-500/30",
    zinc:    "bg-zinc-500/15    text-zinc-300    border-zinc-500/30",
  }[opt.color];
  return (
    <span className={`shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${styles}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80"></span>
      {opt.label}
    </span>
  );
}

